function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useEffect, useMemo, useState } from 'react';
import { startOfDay, subMonths } from 'date-fns';
import isEqual from 'lodash/isEqual';
import cn from 'classnames';
import { DropdownMenu, FilterPicker, MultiSelectFilterPicker, RadioButtonWithLabel, TextButton, Typography, KeyboardDatePicker } from 'seagull';
import styles from "./agreementsDashboardFilters.module.scss";
import { useIoContext, useStoreActions, useStoreApi } from "../../contexts";
import { relationshipSummaryModel, timeModel } from "../../models";
import { selectors } from "../../store/state-selectors";
export let FilterType;

(function (FilterType) {
  FilterType["AgreementStatus"] = "agreementStatus";
  FilterType["EffectiveDate"] = "effectiveDate";
  FilterType["LastBillingDate"] = "lastBillingDate";
  FilterType["PaymentMethod"] = "paymentMethod";
  FilterType["HasCredit"] = "hasCredit";
  FilterType["Completion"] = "completion";
  FilterType["BillingTrigger"] = "billingTrigger";
  FilterType["Recurrence"] = "recurrence";
  FilterType["Automatic"] = "automatic";
  FilterType["ServiceNames"] = "serviceNames";
})(FilterType || (FilterType = {}));

export const defaultAgreementStatusFilter = [relationshipSummaryModel.AgreementStatus.Active, relationshipSummaryModel.AgreementStatus.Amended, relationshipSummaryModel.AgreementStatus.Exhausted];
export function deserializeFiltersFromURL(urlParams) {
  return {
    [FilterType.AgreementStatus]: deserializeFilter(FilterType.AgreementStatus, urlParams.agreementStatus),
    [FilterType.EffectiveDate]: deserializeFilter(FilterType.EffectiveDate, urlParams.effectiveDate),
    [FilterType.LastBillingDate]: deserializeFilter(FilterType.LastBillingDate, urlParams.lastBillingDate),
    [FilterType.PaymentMethod]: deserializeFilter(FilterType.PaymentMethod, urlParams.paymentMethod),
    [FilterType.Completion]: deserializeFilter(FilterType.Completion, urlParams.completion),
    [FilterType.Recurrence]: deserializeFilter(FilterType.Recurrence, urlParams.recurrence),
    [FilterType.HasCredit]: deserializeFilter(FilterType.HasCredit, urlParams.hasCredit),
    [FilterType.Automatic]: deserializeFilter(FilterType.Automatic, urlParams.automatic),
    [FilterType.BillingTrigger]: deserializeFilter(FilterType.BillingTrigger, urlParams.billingTrigger),
    [FilterType.ServiceNames]: deserializeFilter(FilterType.ServiceNames, urlParams.serviceNames)
  };

  function deserializeFilter(type, value) {
    const {
      deserialize
    } = filtersConfig[type];
    if (typeof value !== 'string') return value;
    return deserialize ? deserialize(value) : value;
  }
}
const agreementStatusLabel = 'Agreement status';
const effectiveDateLabel = 'Effective date';
const lastBillingDateLabel = 'Last billing date';
const paymentMethodLabel = 'Payment method';
const completionLabel = 'Service status';
const recurrenceLabel = 'Occurrences';
const hasCreditLabel = 'Has credit';
const automaticLabel = 'Automatic services state';
const billingTriggerLabel = 'Billing trigger';
const serviceNameLabel = 'Service name';
const FilterSelectorMenuTitle = /*#__PURE__*/React.forwardRef((props, ref) => /*#__PURE__*/React.createElement(Typography, {
  ref: ref,
  className: cn(styles.menuTitle, props.className),
  variant: "paragraph2",
  weight: "normal",
  skin: "secondary"
}, props.children));
export function FilterSelector(props) {
  const {
    selected,
    onSelect
  } = props;
  const {
    agreementItems,
    serviceItems
  } = useMemo(() => {
    const getEntry = (type, text) => ({
      type,
      text,
      disabled: selected.includes(type)
    });

    const agreementItems = [getEntry(FilterType.AgreementStatus, agreementStatusLabel), getEntry(FilterType.EffectiveDate, effectiveDateLabel), getEntry(FilterType.LastBillingDate, lastBillingDateLabel), getEntry(FilterType.PaymentMethod, paymentMethodLabel), getEntry(FilterType.HasCredit, hasCreditLabel)];
    const serviceItems = [getEntry(FilterType.ServiceNames, serviceNameLabel), getEntry(FilterType.Completion, completionLabel), getEntry(FilterType.BillingTrigger, billingTriggerLabel), getEntry(FilterType.Recurrence, recurrenceLabel), getEntry(FilterType.Automatic, automaticLabel)];
    return {
      agreementItems,
      serviceItems
    };
  }, [selected]);

  function getMenuItem(type, text, disabled) {
    return /*#__PURE__*/React.createElement(DropdownMenu.Item, {
      key: type,
      skin: "primary",
      "data-testid": "filter-option-".concat(type),
      disabled: disabled,
      onClick: () => onSelect(type)
    }, text);
  }

  return /*#__PURE__*/React.createElement(DropdownMenu, {
    hAlign: "center",
    target: /*#__PURE__*/React.createElement(TextButton, {
      "data-testid": "add-filter",
      underline: "hover"
    }, "+Add filter")
  }, /*#__PURE__*/React.createElement(FilterSelectorMenuTitle, null, "Agreements"), agreementItems.map(_ref => {
    let {
      type,
      text,
      disabled
    } = _ref;
    return getMenuItem(type, text, disabled);
  }), /*#__PURE__*/React.createElement(FilterSelectorMenuTitle, {
    className: styles.marginTop
  }, "Services"), serviceItems.map(_ref2 => {
    let {
      type,
      text,
      disabled
    } = _ref2;
    return getMenuItem(type, text, disabled);
  }));
}
const agreementStatusFilterOptions = [{
  value: relationshipSummaryModel.AgreementStatus.Active,
  label: 'Active'
}, {
  value: relationshipSummaryModel.AgreementStatus.Amended,
  label: 'Amended'
}, {
  value: relationshipSummaryModel.AgreementStatus.Exhausted,
  label: 'Completed'
}, {
  value: relationshipSummaryModel.AgreementStatus.Terminated,
  label: 'Terminated'
}];

function AgreementStatusFilter(props) {
  return /*#__PURE__*/React.createElement(MultiSelectFilterPicker, {
    removable: true,
    startOpen: props.startOpen,
    "data-testid": "agreement-status-filter",
    value: props.value,
    options: agreementStatusFilterOptions,
    label: agreementStatusLabel,
    onChange: props.onChange,
    onRemove: props.onRemove
  });
}

function EffectiveDateFilter(props) {
  const {
    value,
    startOpen,
    onChange,
    onRemove,
    onInputBlur,
    onInputFocus
  } = props;
  const {
    getNow
  } = useIoContext();
  const hasDatesFromProps = !!(value.fromDate && value.toDate);
  const [selectingRange, setSelectingRange] = useState(hasDatesFromProps);
  const [fromDate, setFromDate] = useState(getInitialFromDate);
  const [toDate, setToDate] = useState(getInitialToDate);
  useEffect(() => {
    value.fromDate && setFromDate(value.fromDate);
  }, [value.fromDate]);
  useEffect(() => {
    value.toDate && setToDate(value.toDate);
  }, [value.toDate]);
  const label = useMemo(() => {
    if (value.fromDate && value.toDate) {
      return "".concat(effectiveDateLabel, " (").concat(timeModel.formatDate(value.fromDate), "-").concat(timeModel.formatDate(value.toDate), ")");
    }

    return effectiveDateLabel;
  }, [value.fromDate, value.toDate]);

  function onChangeIfDifferentValue(newValue) {
    if (!isEqual(newValue, value)) {
      onChange(newValue);
    }
  }

  function getInitialFromDate() {
    return value.fromDate || subMonths(getNow(), 1);
  }

  function getInitialToDate() {
    return value.toDate || getNow();
  }

  return /*#__PURE__*/React.createElement(FilterPicker, {
    removable: true,
    startOpen: startOpen,
    "data-testid": "effective-date-filter",
    disableApply: selectingRange && fromDate && toDate && fromDate.getTime() > toDate.getTime(),
    label: label,
    title: effectiveDateLabel,
    highlightTrigger: !!(value.fromDate && value.toDate),
    onRemove: onRemove,
    onApply: () => onChangeIfDifferentValue(selectingRange ? {
      fromDate,
      toDate
    } : {}),
    onClose: opts => {
      if (opts !== null && opts !== void 0 && opts.revert) {
        setSelectingRange(hasDatesFromProps);
        setFromDate(getInitialFromDate());
        setToDate(getInitialToDate());
      }
    }
  }, /*#__PURE__*/React.createElement(RadioButtonWithLabel, {
    label: "Select all",
    testId: "effective-date-filter-all",
    checked: !selectingRange,
    onChange: () => setSelectingRange(false)
  }), /*#__PURE__*/React.createElement(RadioButtonWithLabel, {
    label: "Specific range",
    testId: "effective-date-filter-range",
    checked: selectingRange,
    onChange: () => {
      setSelectingRange(true);
      const now = startOfDay(getNow());

      if (!fromDate) {
        setFromDate(now);
      }

      if (!toDate) {
        setToDate(now);
      }
    }
  }), selectingRange && fromDate && toDate && /*#__PURE__*/React.createElement(DateRangePicker, {
    testid: "effective-date-filter",
    fromDate: fromDate,
    toDate: toDate,
    onChange: data => {
      setFromDate(data.fromDate);
      setToDate(data.toDate);
    },
    onInputFocus: onInputFocus,
    onInputBlur: onInputBlur
  }));
}

export let LastBillingDateType;

(function (LastBillingDateType) {
  LastBillingDateType["None"] = "none";
  LastBillingDateType["Never"] = "never";
  LastBillingDateType["Ever"] = "ever";
  LastBillingDateType["Range"] = "range";
})(LastBillingDateType || (LastBillingDateType = {}));

function getLastBillingDateOptionLabel(type) {
  switch (type) {
    case LastBillingDateType.Ever:
      return 'Ever billed';

    case LastBillingDateType.Never:
      return 'Never billed';

    case LastBillingDateType.Range:
      return 'Specific range';

    case LastBillingDateType.None:
      return '';
  }
}

function LastBillingDateFilter(props) {
  const {
    value,
    startOpen,
    onChange,
    onRemove,
    onInputBlur,
    onInputFocus
  } = props;
  const {
    getNow
  } = useIoContext();
  const [type, setType] = useState(value.type);
  const [fromDate, setFromDate] = useState(getInitialFromDate);
  const [toDate, setToDate] = useState(getInitialToDate);
  const disableApply = fromDate && toDate && fromDate.getTime() > toDate.getTime();
  useEffect(() => {
    setType(value.type);
  }, [value.type]);
  useEffect(() => {
    value.fromDate && setFromDate(value.fromDate);
  }, [value.fromDate]);
  useEffect(() => {
    value.toDate && setToDate(value.toDate);
  }, [value.toDate]);
  const label = useMemo(() => {
    switch (value.type) {
      case LastBillingDateType.Ever:
      case LastBillingDateType.Never:
        return "".concat(lastBillingDateLabel, " (").concat(getLastBillingDateOptionLabel(value.type), ")");

      case LastBillingDateType.Range:
        return "".concat(lastBillingDateLabel, " (").concat(timeModel.formatDate(value.fromDate), "-").concat(timeModel.formatDate(value.toDate), ")");

      case LastBillingDateType.None:
        return lastBillingDateLabel;
    }
  }, [value]);

  function onChangeIfDifferentValue(newValue) {
    if (!isEqual(newValue, value)) {
      onChange(newValue);
    }
  }

  function getInitialFromDate() {
    return value.fromDate || subMonths(getNow(), 1);
  }

  function getInitialToDate() {
    return value.toDate || getNow();
  }

  return /*#__PURE__*/React.createElement(FilterPicker, {
    removable: true,
    startOpen: startOpen,
    "data-testid": "last-billing-date-filter",
    disableApply: disableApply,
    label: label,
    title: lastBillingDateLabel,
    highlightTrigger: value.type !== LastBillingDateType.None,
    onRemove: onRemove,
    onApply: () => {
      if (type === LastBillingDateType.Range) {
        fromDate && toDate && onChangeIfDifferentValue({
          type,
          fromDate,
          toDate
        });
      } else {
        onChangeIfDifferentValue({
          type
        });
      }
    },
    onClose: opts => {
      if (opts !== null && opts !== void 0 && opts.revert) {
        setType(value.type);
        setFromDate(getInitialFromDate());
        setToDate(getInitialToDate());
      }
    }
  }, /*#__PURE__*/React.createElement(RadioButtonWithLabel, {
    label: "Select all",
    testId: "last-billing-date-filter-all",
    checked: type === LastBillingDateType.None,
    onChange: () => setType(LastBillingDateType.None)
  }), /*#__PURE__*/React.createElement(RadioButtonWithLabel, {
    label: getLastBillingDateOptionLabel(LastBillingDateType.Ever),
    testId: "last-billing-date-filter-ever",
    checked: type === LastBillingDateType.Ever,
    onChange: () => setType(LastBillingDateType.Ever)
  }), /*#__PURE__*/React.createElement(RadioButtonWithLabel, {
    label: getLastBillingDateOptionLabel(LastBillingDateType.Never),
    testId: "last-billing-date-filter-never",
    checked: type === LastBillingDateType.Never,
    onChange: () => setType(LastBillingDateType.Never)
  }), /*#__PURE__*/React.createElement(RadioButtonWithLabel, {
    label: getLastBillingDateOptionLabel(LastBillingDateType.Range),
    testId: "last-billing-date-filter-range",
    checked: type === LastBillingDateType.Range,
    onChange: () => {
      setType(LastBillingDateType.Range);
      const now = startOfDay(getNow());

      if (!fromDate) {
        setFromDate(now);
      }

      if (!toDate) {
        setToDate(now);
      }
    }
  }), type === LastBillingDateType.Range && fromDate && toDate && /*#__PURE__*/React.createElement(DateRangePicker, {
    testid: "last-billing-date-filter",
    fromDate: fromDate,
    toDate: toDate,
    onChange: data => {
      setFromDate(data.fromDate);
      setToDate(data.toDate);
    },
    onInputFocus: onInputFocus,
    onInputBlur: onInputBlur
  }));
}

export let PaymentMethodFilterValue;

(function (PaymentMethodFilterValue) {
  PaymentMethodFilterValue["CreditCard"] = "credit";
  PaymentMethodFilterValue["BankAccount"] = "bank";
  PaymentMethodFilterValue["VerificationInitiated"] = "verificationInitiated";
  PaymentMethodFilterValue["VerificationFailed"] = "verificationFailed";
  PaymentMethodFilterValue["NotSet"] = "notSet";
})(PaymentMethodFilterValue || (PaymentMethodFilterValue = {}));

const paymentMethodFilterOptions = [{
  value: PaymentMethodFilterValue.CreditCard,
  label: 'Credit card'
}, {
  value: PaymentMethodFilterValue.BankAccount,
  label: 'Bank account'
}, {
  value: PaymentMethodFilterValue.VerificationInitiated,
  label: 'Manual verification initiated'
}, {
  value: PaymentMethodFilterValue.VerificationFailed,
  label: 'Manual verification failed'
}, {
  value: PaymentMethodFilterValue.NotSet,
  label: 'Not set'
}];

function PaymentMethodFilter(props) {
  return /*#__PURE__*/React.createElement(MultiSelectFilterPicker, {
    removable: true,
    startOpen: props.startOpen,
    "data-testid": "payment-method-filter",
    value: props.value,
    options: paymentMethodFilterOptions,
    label: paymentMethodLabel,
    onChange: props.onChange,
    onRemove: props.onRemove
  });
}

export let CompletionFilterValue;

(function (CompletionFilterValue) {
  CompletionFilterValue["Completed"] = "completed";
  CompletionFilterValue["NotCompleted"] = "notCompleted";
})(CompletionFilterValue || (CompletionFilterValue = {}));

const completionFilterOptions = [{
  value: CompletionFilterValue.NotCompleted,
  label: 'Not completed'
}, {
  value: CompletionFilterValue.Completed,
  label: 'Completed'
}];

function CompletionFilter(props) {
  return /*#__PURE__*/React.createElement(MultiSelectFilterPicker, _extends({
    removable: true,
    "data-testid": "completion-filter",
    options: completionFilterOptions,
    label: completionLabel
  }, props));
}

export let RecurrenceFilterValue;

(function (RecurrenceFilterValue) {
  RecurrenceFilterValue["OneTime"] = "oneTime";
  RecurrenceFilterValue["Weekly"] = "weekly";
  RecurrenceFilterValue["Biweekly"] = "biweekly";
  RecurrenceFilterValue["Monthly"] = "monthly";
  RecurrenceFilterValue["Quarterly"] = "quarterly";
  RecurrenceFilterValue["Yearly"] = "yearly";
})(RecurrenceFilterValue || (RecurrenceFilterValue = {}));

const recurrenceFilterOptions = [{
  value: RecurrenceFilterValue.OneTime,
  label: 'One time'
}, {
  value: RecurrenceFilterValue.Weekly,
  label: 'Weekly'
}, {
  value: RecurrenceFilterValue.Biweekly,
  label: 'Bi-weekly'
}, {
  value: RecurrenceFilterValue.Monthly,
  label: 'Monthly'
}, {
  value: RecurrenceFilterValue.Quarterly,
  label: 'Quarterly'
}, {
  value: RecurrenceFilterValue.Yearly,
  label: 'Yearly'
}];

function RecurrenceFilter(props) {
  return /*#__PURE__*/React.createElement(MultiSelectFilterPicker, _extends({
    removable: true,
    "data-testid": "recurrence-filter",
    options: recurrenceFilterOptions,
    label: recurrenceLabel
  }, props));
}

export let HasCreditFilterValue;

(function (HasCreditFilterValue) {
  HasCreditFilterValue["Yes"] = "yes";
  HasCreditFilterValue["No"] = "no";
})(HasCreditFilterValue || (HasCreditFilterValue = {}));

const hasCreditFilterOptions = [{
  value: HasCreditFilterValue.Yes,
  label: 'Yes'
}, {
  value: HasCreditFilterValue.No,
  label: 'No'
}];

function HasCreditFilter(props) {
  return /*#__PURE__*/React.createElement(MultiSelectFilterPicker, _extends({
    removable: true,
    "data-testid": "has-credit-filter",
    options: hasCreditFilterOptions,
    label: hasCreditLabel
  }, props));
}

export let AutomaticFilterValue;

(function (AutomaticFilterValue) {
  AutomaticFilterValue["Active"] = "active";
  AutomaticFilterValue["Paused"] = "paused";
})(AutomaticFilterValue || (AutomaticFilterValue = {}));

const automaticFilterOptions = [{
  value: AutomaticFilterValue.Active,
  label: 'Active'
}, {
  value: AutomaticFilterValue.Paused,
  label: 'Paused'
}];

function AutomaticFilter(props) {
  return /*#__PURE__*/React.createElement(MultiSelectFilterPicker, _extends({
    removable: true,
    "data-testid": "automatic-filter",
    options: automaticFilterOptions,
    label: automaticLabel
  }, props));
}

export let BillingTriggerFilterValue;

(function (BillingTriggerFilterValue) {
  BillingTriggerFilterValue["Automatic"] = "automatic";
  BillingTriggerFilterValue["Manual"] = "manual";
})(BillingTriggerFilterValue || (BillingTriggerFilterValue = {}));

const billingTriggerFilterOptions = [{
  value: BillingTriggerFilterValue.Automatic,
  label: 'Automatic'
}, {
  value: BillingTriggerFilterValue.Manual,
  label: 'Manual'
}];

function BillingTriggerFilter(props) {
  return /*#__PURE__*/React.createElement(MultiSelectFilterPicker, _extends({
    removable: true,
    "data-testid": "billing-trigger-filter",
    options: billingTriggerFilterOptions,
    label: billingTriggerLabel
  }, props));
}

function DateRangePicker(props) {
  const {
    fromDate,
    toDate,
    testid,
    onChange,
    onInputBlur,
    onInputFocus
  } = props;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(KeyboardDatePicker, {
    label: "From",
    "data-testid": testid && "".concat(testid, "-from"),
    value: fromDate,
    maxDate: toDate,
    onChange: function (_onChange) {
      function onChange(_x) {
        return _onChange.apply(this, arguments);
      }

      onChange.toString = function () {
        return _onChange.toString();
      };

      return onChange;
    }(date => date && onChange && onChange({
      fromDate: date,
      toDate
    })),
    onFocus: onInputFocus,
    onBlur: onInputBlur
  }), /*#__PURE__*/React.createElement(KeyboardDatePicker, {
    label: "To",
    "data-testid": testid && "".concat(testid, "-to"),
    value: toDate,
    minDate: fromDate,
    onChange: function (_onChange2) {
      function onChange(_x2) {
        return _onChange2.apply(this, arguments);
      }

      onChange.toString = function () {
        return _onChange2.toString();
      };

      return onChange;
    }(date => date && onChange && onChange({
      fromDate,
      toDate: date
    })),
    onFocus: onInputFocus,
    onBlur: onInputBlur
  }));
}

function ServiceNamesFilter(props) {
  const {
    api
  } = useStoreActions();
  const {
    getState
  } = useStoreApi();
  const [options, setOptions] = useState([]);
  useEffect(() => {
    const businessId = selectors.user.businessId(getState());
    const isVendor = selectors.user.isVendor(getState());

    if (businessId) {
      api.relationship.getServiceNames(businessId, isVendor, 'agreement').then(names => {
        setOptions(names.map(n => ({
          label: n,
          value: n
        })));
      });
    }
  }, []);
  return /*#__PURE__*/React.createElement(MultiSelectFilterPicker, _extends({
    removable: true,
    withSearchbox: true,
    "data-testid": "service-names-filter",
    options: options,
    label: serviceNameLabel
  }, props, {
    onInputFocus: props.onInputFocus,
    onInputBlur: props.onInputBlur
  }));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getSimpleMultiSelectFilterConfig(type, component) {
  return {
    component,

    hasValue(v) {
      return v.length > 0;
    },

    getEmptyValue() {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return [];
    },

    getRequestParam(v) {
      return {
        [type]: v.length ? v.join(',') : undefined
      };
    }

  };
}

const effectiveDateConfig = {
  component: EffectiveDateFilter,

  hasValue(effectiveDate) {
    return !!(effectiveDate.fromDate && effectiveDate.toDate);
  },

  getEmptyValue() {
    return {};
  },

  getRequestParam(effectiveDate) {
    return {
      fromDate: effectiveDate.fromDate && timeModel.toDatestampString(effectiveDate.fromDate),
      toDate: effectiveDate.toDate && timeModel.toDatestampString(effectiveDate.toDate)
    };
  },

  serialize(_ref3) {
    let {
      fromDate,
      toDate
    } = _ref3;
    return fromDate && toDate ? "".concat(timeModel.toDatestampString(fromDate), ",").concat(timeModel.toDatestampString(toDate)) : '';
  },

  deserialize(str) {
    const splitted = str.split(',');
    return splitted.length === 2 ? {
      fromDate: timeModel.fromDatestampString(splitted[0]),
      toDate: timeModel.fromDatestampString(splitted[1])
    } : {};
  }

};
const lastBillingDateConfig = {
  component: LastBillingDateFilter,

  hasValue(value) {
    return value.type !== LastBillingDateType.None;
  },

  getEmptyValue() {
    return {
      type: LastBillingDateType.None
    };
  },

  getRequestParam(lastBillingDate) {
    const isLastBilledWithinRange = lastBillingDate.type === LastBillingDateType.Range;
    return {
      lastBilled: lastBillingDate.type !== LastBillingDateType.None ? lastBillingDate.type : undefined,
      lastBilledFrom: isLastBilledWithinRange ? timeModel.toDatestampString(lastBillingDate.fromDate) : undefined,
      lastBilledTo: isLastBilledWithinRange ? timeModel.toDatestampString(lastBillingDate.toDate) : undefined
    };
  },

  serialize(data) {
    if (data.type === LastBillingDateType.None) {
      return '';
    }

    if (data.type === LastBillingDateType.Range) {
      return "".concat(timeModel.toDatestampString(data.fromDate), ",").concat(timeModel.toDatestampString(data.toDate));
    }

    return data.type;
  },

  deserialize(lastBilled) {
    if (!lastBilled) {
      return {
        type: LastBillingDateType.None
      };
    }

    if (lastBilled === LastBillingDateType.Ever || lastBilled === LastBillingDateType.Never) {
      return {
        type: lastBilled
      };
    }

    const dates = lastBilled.split(',');

    if (dates.length !== 2) {
      return {
        type: LastBillingDateType.None
      };
    }

    const fromDate = timeModel.fromDatestampString(dates[0]);
    const toDate = timeModel.fromDatestampString(dates[1]);
    return {
      type: LastBillingDateType.Range,
      fromDate,
      toDate
    };
  }

};
export const filtersConfig = {
  [FilterType.EffectiveDate]: effectiveDateConfig,
  [FilterType.LastBillingDate]: lastBillingDateConfig,
  [FilterType.AgreementStatus]: getSimpleMultiSelectFilterConfig(FilterType.AgreementStatus, AgreementStatusFilter),
  [FilterType.PaymentMethod]: getSimpleMultiSelectFilterConfig(FilterType.PaymentMethod, PaymentMethodFilter),
  [FilterType.Completion]: getSimpleMultiSelectFilterConfig(FilterType.Completion, CompletionFilter),
  [FilterType.Recurrence]: getSimpleMultiSelectFilterConfig(FilterType.Recurrence, RecurrenceFilter),
  [FilterType.HasCredit]: getSimpleMultiSelectFilterConfig(FilterType.HasCredit, HasCreditFilter),
  [FilterType.Automatic]: getSimpleMultiSelectFilterConfig(FilterType.Automatic, AutomaticFilter),
  [FilterType.BillingTrigger]: getSimpleMultiSelectFilterConfig(FilterType.BillingTrigger, BillingTriggerFilter),
  [FilterType.ServiceNames]: getSimpleMultiSelectFilterConfig(FilterType.ServiceNames, ServiceNamesFilter)
};
export function serializeFilter(type, value) {
  const {
    serialize
  } = filtersConfig[type];
  return serialize ? serialize(value) : value;
}